import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { cloneDeep } from 'lodash';
import moment from 'moment/min/moment-with-locales';
import { confirmAlert } from 'react-confirm-alert';

//componets
import applicationRouter from '~/hoc/applicationRouter'
import withLocalization from '~/hoc/withLocalization';
import AbsencesTotals from './AbsencesTotals';
import GenericForm from '~/components/GenericForm/GenericForm';
import UserPromptModal from '../../../components/GenericModal/userPromptModal/index.js';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';

//utils
import customerFeature from '~/utils/customerFeature';
import { DEFAULT_ABSENCE_TYPES, payrollIntegrationOptions } from '../../../utils/constants';
import { getDateAsPerServer } from '../../../utils/commonMethods';

//schema
import getSchema from '~/library/schemas/absence';

const defaultUiSchema = ({ user_type, config }) => {
    if (user_type === 'member')
        return {
            'ui:field': 'layout',
            'ui:layout:hideframe': true,
            'ui:layout': [
                {
                    absence_type: { xs: 6, sm: 2, className: "mt-sm-2" },
                    leave_type: { xs: 6, sm: 2, className: "mt-sm-2" },
                    project_id: { xs: 6, sm: 4, md: 4 },
                    child: { xs: 6, sm: 4, doShow: ({ formData }) => formData.absence_type === 'child', className: "mt-sm-2" },
                    is_work_related_leave: {
                        xs: 6, sm: 4, className: "mt-sm-2",
                        doShow: ({ formData }) => {
                            const allowWorkRelatedQueriesForAbsence = config?.client?.data?.basicRules?.allowWorkRelatedQueriesForAbsence;
                            const workRelatedAbsenceTypes = config?.client?.data?.basicRules?.workRelatedAbsenceTypes || [];
                            return allowWorkRelatedQueriesForAbsence && workRelatedAbsenceTypes.includes(formData.absence_type)
                        }
                    },
                    social_welfare_leave_type: { xs: 6, sm: 4, doShow: ({ formData }) => formData.absence_type === 'social-welfare' },
                },
                {
                    'ui:subtitle': 'Absence Information*',
                    'ui:className': 'profile-edit-form__group',
                    from: { xs: 6, sm: 3, md: 2, className: 'text-sm' },
                    to: { xs: 6, sm: 3, md: 2 },
                    start_time: { md: 2, xs: 6 },
                    end_time: { md: 2, xs: 6 },
                    hours: { xs: 6, sm: 3, md: 2 },
                    total_hours: { xs: 6, sm: 3, md: 2 },
                    absence_info_group_error: {
                        'ui:widget': 'hidden',
                        classNames: 'd-block text-danger mt-2',
                        xs: 12, sm: 12
                    },
                },
                {
                    total_days: { xs: 6, sm: 3, md: 3, className: "mt-2" },
                    employement_grade: { xs: 6, sm: 3, md: 3, className: "mt-2" },
                    absence_payment_per_day: { xs: 6, sm: 3, md: 3, className: "mt-2" },
                    absence_total_payment: { xs: 6, sm: 3, md: 3, className: "mt-2" },
                },
                {
                    'ui:key': 'payroll-integration-section',
                    'ui:subtitle': 'Payroll integration info',
                    'ui:className': 'custom_style_integration_details py-2',
                    data: { xs: 12, sm: 8 },
                },
                {
                    'ui:className': 'mt-4',
                    comments: { md: 12 },
                },
                {
                    'ui:subtitle': 'Address information (optional)',
                    'ui:className': 'profile-edit-form__group',
                    address: { xs: 6, sm: 3, md: 3 },
                    post_place: { xs: 6, sm: 3, md: 3 },
                    post_number: { xs: 6, sm: 3, md: 2 },
                    gps_data: { xs: 6, sm: 3, md: 4 },
                },
                {
                    'ui:className': 'mt-2',
                    attachments: { md: 12 },
                },
            ],
            absence_type: {
                'ui:widget': 'AbsenceStatuses',
            },
            absence_info_group_error: {
                'ui:widget': () => <></>,
                'ui:label': ''
            },
            leave_type: {
                'ui:widget': 'LeaveTypeWidget',
            },
            child: {
                'ui:widget': 'KidsSelect',
            },
            attachments: {},
            user_id: {
                'ui:widget': 'MembersWidget',
            },
            project_id: {
                'ui:widget': 'ProjectsWidget',
            },
            start_time: {
                'ui:widget': 'InputTime',
                'ui:valueInInt': true,
            },
            end_time: {
                'ui:widget': 'InputTime',
                'ui:valueInInt': true,
            },
            gps_data: {
                'ui:widget': 'GpsCoordinatesWidget',
            },
            data: {
                medical_certification_percent: {
                    'ui:disabled': true,
                },
                doctor_certification_date: {
                    'ui:disabled': true,
                },
            },
            is_work_related_leave: {
                'ui:widget': 'Radio',
            },
            social_welfare_leave_type: {
                'ui:widget': 'SimpleSelectWidget',
                'ui:options': []
            }
        }


    return {
        'ui:field': 'layout',
        'ui:layout:hideframe': true,
        'ui:layout': [
            {
                child: {
                    xs: { span: 6, order: 2 }, sm: 4,
                    doShow: ({ formData }) => formData.absence_type === 'child', className: "mt-sm-2"
                },
            },
            {
                absence_type: { xs: 6, sm: 4, md: 4, className: "mt-sm-2" },
                leave_type: { xs: 6, sm: 4, md: 2, className: "mt-sm-2" },
                project_id: { xs: 6, sm: 4, md: 3, className: "mt-sm-2" },
                employement_grade: { xs: 6, sm: 4, md: 3, className: "mt-sm-2" },
                is_work_related_leave: {
                    xs: 6, sm: 4, className: "mt-sm-2",
                    doShow: ({ formData }) => {
                        const allowWorkRelatedQueriesForAbsence = config?.client?.data?.basicRules?.allowWorkRelatedQueriesForAbsence;
                        const workRelatedAbsenceTypes = config?.client?.data?.basicRules?.workRelatedAbsenceTypes || [];
                        return allowWorkRelatedQueriesForAbsence && workRelatedAbsenceTypes.includes(formData.absence_type)
                    }
                },
                social_welfare_leave_type: { xs: 6, sm: 4, doShow: ({ formData }) => formData.absence_type === 'social-welfare' }
            },
            {
                'ui:subtitle': 'Absence Information*',
                'ui:className': 'profile-edit-form__group',
                from: { xs: 6, sm: 3, md: 2 },
                to: { xs: 6, sm: 3, md: 2 },
                start_time: { xs: 6, sm: 3, md: 2 },
                end_time: { xs: 6, sm: 3, md: 2 },
                hours: { xs: 6, sm: 3, md: 2 },
                total_hours: { xs: 6, sm: 3, md: 2 },
                absence_info_group_error: {
                    'ui:widget': 'hidden',
                    classNames: 'd-block text-danger mt-2',
                    xs: 12, sm: 12
                },
            },
            {
                total_days: { xs: 6, sm: 3, md: 4, className: "mt-2" },
                absence_payment_per_day: { xs: 6, sm: 3, md: 4, className: "mt-2" },
                absence_total_payment: { xs: 12, sm: 3, md: 4, className: "mt-2" }
            },
            {
                'ui:key': 'payroll-integration-section',
                'ui:subtitle': 'Payroll integration info',
                'ui:className': 'custom_style_integration_details py-2',
                data: { xs: 12, sm: 8 },
            },
            {
                'ui:className': 'mt-4',
                comments: { md: 12 },
            },
            {
                'ui:subtitle': 'Address information (optional)',
                'ui:className': 'profile-edit-form__group',
                address: { md: 3, xs: 6 },
                post_place: { md: 3, xs: 6 },
                post_number: { md: 2, xs: 6 },
                gps_data: { md: 4, xs: 6 },
            },
            {
                'ui:className': 'mt-2',
                attachments: { md: 12 },
            },
        ],
        absence_type: {
            'ui:widget': 'AbsenceStatuses',
        },
        absence_info_group_error: {
            'ui:widget': () => <div></div>,
            'ui:label': ''
        },
        leave_type: {
            'ui:widget': 'LeaveTypeWidget',
        },
        child: {
            'ui:widget': 'KidsSelect',
        },
        attachments: {},
        user_id: {
            'ui:widget': 'MembersWidget',
        },
        project_id: {
            'ui:widget': 'ProjectsWidget',
            'ui:user_id': null
        },
        start_time: {
            'ui:widget': 'InputTime',
            'ui:valueInInt': true,
        },
        end_time: {
            'ui:widget': 'InputTime',
            'ui:valueInInt': true,
        },
        gps_data: {
            'ui:widget': 'GpsCoordinatesWidget',
        },
        data: {
            medical_certification_percent: {
                'ui:disabled': true,
            },
            doctor_certification_date: {
                'ui:disabled': true,
            },
        },
        is_work_related_leave: {
            'ui:widget': 'Radio',
        },
        social_welfare_leave_type: {
            'ui:widget': 'SimpleSelectWidget',
            'ui:options': []
        }
    }
};
class AbsencesForm extends Component {
    constructor(props) {
        super(props);
        this.defaultHolidaysForVacation = props.commonStore.config.client.data.Vacation_setup.default_holidays_for_vacation || '';
        this.enableDefaultHolidaysForAbsence = !!props.commonStore.config.client.data.Vacation_setup.enable_default_holidays_for_absence;
        this.state = {
            schema: getSchema({
                user_type: this.props.userStore.currentUser.user_type,
                config: this.props.commonStore.config
            }),
            uiSchema: defaultUiSchema({ user_type: this.props.userStore.currentUser.user_type, config: this.props.commonStore.config }),
            formTimeStamp: Date.now(),
            forceFormUpdate: Date.now(),
            userPromptModal: {
                source: '',
                header: '',
                showModal: false,
                sourceInfo: {},
                actionMethods: {}
            }
        };
        this.onSave = this.onSave.bind(this);
    }

    getListUrl() {
        return '/admin/absences';
    }

    async handleChangeStatus() {
        this.loadData();
    }

    async loadData() {
        const { absenceStore, location, commonStore } = this.props;
        const timezoneGMT = commonStore.config && commonStore.config.timezoneGMT ? commonStore.config.timezoneGMT : 0;
        const { IASickLeavesEnabled } = commonStore.config.client.data.basicRules;
        const currentDate = moment(getDateAsPerServer(timezoneGMT)).format('YYYY-MM-DD');
        this.setState({
            schema: getSchema({
                user_type: this.props.userStore.currentUser.user_type,
                config: this.props.commonStore.config
            }),
        });

        let getId = (this.props.router.location.state && this.props.router.location.state.absence) || null;
        if (!getId) {
            this.setState({ uiSchema: this.prepareSchema(defaultUiSchema({ user_type: this.props.userStore.currentUser.user_type, config: this.props.commonStore.config }), null) });
            return absenceStore.returnDefaultNew({
                location,
                user_id: this.props.userStore.currentUser.id,
                currentDate,
                absence_type: IASickLeavesEnabled ? DEFAULT_ABSENCE_TYPES.IA_SICK_LEAVE : DEFAULT_ABSENCE_TYPES.SICK_LEAVE
            });
        }
        else {
            const absence = await absenceStore.load(getId);
            this.setState({ uiSchema: this.prepareSchema(defaultUiSchema({ user_type: this.props.userStore.currentUser.user_type, config: this.props.commonStore.config }), absence.absence) });
        }
    }

    prepareSchema(_schema, currentEntity) {
        const schema = cloneDeep(_schema);
        const { userStore } = this.props;
        const { currentUser } = userStore;

        schema.attachments = {
            'ui:widget': 'AttachmentsWidget',
            'ui:imageContext': {
                model: 'Absence',
                fileType: 'docs',
                id: currentEntity && currentEntity.id !== 'add' ? currentEntity.id : 0,
                existingAttachment: currentEntity && currentEntity.Attachments ? currentEntity.Attachments : [],
            },
        };
        const showUser = (currentUser.user_type === 'admin' || currentUser.user_type === 'pm')
            && customerFeature('pm_allow_editing_all')
            && !customerFeature('disallow_absences_vacations_not_self');

        if (showUser) {
            schema['ui:layout'][0].user_id = { xs: 6, sm: 4, className: "mt-sm-2" };
        }

        if (!!currentEntity) {
            schema.user_id = {
                ...schema.user_id,
                'ui:disabled': true
            };
        }

        if (currentUser.user_type === 'member') {
            if (schema.user_id) schema.user_id.classNames = 'hidden';
        }

        if (currentUser.user_type === 'pm') {
            schema.project_id = {
                'ui:widget': 'ProjectsWidget',
                'ui:user_id': currentUser.id
            }
        }

        if (currentEntity && currentEntity.absence_type) {
            schema.absence_type['ui:widget'] = 'AbsenceStatusesOnEdit';
            schema.leave_type['ui:widget'] = 'LeaveTypeWidgetOnEdit';
        }

        if (schema.social_welfare_leave_type) {
            const { commonStore } = this.props;

            const socialWelfareSubTypes = commonStore.config.client.data?.basicRules?.socialWelfareSubTypes || [];
            const options = socialWelfareSubTypes ? socialWelfareSubTypes.map(e => {
                return { label: e.name, value: e.code };
            }) : [];

            schema.social_welfare_leave_type = {
                'ui:widget': 'SimpleSelectWidget',
                'ui:options': options
            }
        }

        return schema;
    }

    componentWillMount() {
        this.props.absenceStore.loading = true;
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(previousProps) {
        const { location } = this.props;
        if (location !== previousProps.location) {
            this.loadData();
        }
    }

    handleCloseUserPromptModal = () => {
        this.setState({ userPromptModal: { ...this.state.userPromptModal, showModal: false } })
    }

    handleRejectSystemTimesheets = (values, timesheetIds) => {
        this.handleCloseUserPromptModal();
        this.onSave({ ...values, rejectSystemTimesheetIds: timesheetIds });
    }

    onSave(values) {
        const { absenceStore, t, commonStore, userStore } = this.props;
        const { currentUser } = userStore;
        const { biztype } = commonStore.config;
        try {
            let getId = (this.props.router.location.state && this.props.router.location.state.absence) || null;
            if (currentUser.user_type === 'member' && !getId) {
                values.user_id = currentUser.id;
            }
            const allow = (currentUser.user_type === 'admin' || currentUser.user_type === 'pm')
                && customerFeature('pm_allow_editing_all')
                && !customerFeature('disallow_absences_vacations_not_self');

            if (currentUser.user_type === 'admin' && values.user_id === currentUser.id) {
                commonStore.addNotification(t('Select User'), null, 'error');
                return false;
            }
            if (currentUser.id !== values.user_id && !allow) {
                commonStore.addNotification(t('Can not save on behalf of others'), null, 'error');
                return false;
            }
            if (!values.total_days) {
                return commonStore.addNotification(t("Total days should be at least 1 day, please check if you have selected holidays"), null, 'warning');
            }
            if (values.absence_type === DEFAULT_ABSENCE_TYPES.MEDICAL_LEAVE
                && !values.data.medical_certification_percent && !values.data.doctor_certification_date) {
                commonStore.addNotification(t("Please add Doctor certificate details if you want to sync this to integration system"), null, 'warning');
            }
            if (!values.project_id && biztype.data && biztype.data.allow_multiple_branches) {
                return commonStore.addNotification(t("Please select Project (Branch) and then try again"), null, 'warning');
            }
            if ((values.totalHours * 60) < 10) {
                return commonStore.addNotification(t("The entered duration is too short."), null, 'warning');
            }
            delete values.totalHours; // remove extra field added for validation
            return absenceStore.save(values, !getId).then(result => {
                if (result?.userPrompts?.length) {
                    const systemTimesheetAndAbsenceIntersection = result.userPrompts.find(ele => ele.source === 'systemTimesheetAndAbsenceIntersection')
                    const timesheetIds = result.systemTimesheetIds || [];
                    if (systemTimesheetAndAbsenceIntersection?.showModal) {
                        this.setState({
                            userPromptModal: {
                                ...systemTimesheetAndAbsenceIntersection,
                                actionMethods: {
                                    handleCloseUserPromptModal: this.handleCloseUserPromptModal,
                                    handleRejectSystemTimesheets: () => this.handleRejectSystemTimesheets(values, timesheetIds)
                                }
                            }
                        });
                    }

                    const intersection = result.userPrompts.find(ele => ele.source === 'intersection')
                    if (intersection?.showModal) {
                        this.setState({
                            userPromptModal: {
                                ...intersection,
                                actionMethods: {
                                    handleCloseUserPromptModal: this.handleCloseUserPromptModal
                                }
                            }
                        });
                    }
                    return false;
                }
                if (result.registeredTimelogs?.length) {
                    this.showConfirmAlert(
                        () => {
                            this.onSave({
                                ...values,
                                isConfirmedType: 'timelogsToReject',
                            });
                        }
                    );
                    return false;
                }
                if (!result.absence || !result.absence.id) {
                    commonStore.addNotification(t(result.message || 'Error'), null, 'error');
                    return false;
                }
                this.props.absenceStore.setLastInteractedId(result.absence.id);
                const cUser = this.props.userStore.currentUser;
                if (result.absence.user_id === cUser.id) {
                    this.props.userStore.pullUser().then(() => {
                        commonStore.addNotification(t(result?.message) || t('Saved'), null, 'success', null, result?.message?.length > 10 ? 5000 : 1000);
                        this.getBack()
                        return true;
                    });
                } else {
                    commonStore.addNotification(t(result?.message) || t('Saved'), null, 'success', null, result?.message?.length > 10 ? 5000 : 1000);
                    this.getBack()
                    return true;
                }
            }).catch(e => {
                console.error(e);
            });
        }
        catch (err) {
            console.error(err)
        }
    }

    showConfirmAlert = (onConfirm) => {
        const { t } = this.props;
        confirmAlert({
            title: t('Are You Sure'),
            message: t('System time logs are already registered for these dates. Would you like to reject them and add the leave instead?'),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: onConfirm
                },
                {
                    label: t('Cancel'),
                    onClick: () => { }
                }
            ]
        });
    }

    validateAbsence(formData, errors) {
        const { t, commonStore } = this.props;
        const { config } = commonStore
        const { IASickLeaveMaxDaysInPeriod, selfClarificationDaysInPeriod, selfClarificationCycleInMonths } = config?.client?.data?.basicRules;
        if (formData.leave_type && formData.leave_type === 'paid' && formData.absence_type) {
            if (formData.absence_type === DEFAULT_ABSENCE_TYPES.SICK_LEAVE) {
                if (formData.total_days > selfClarificationDaysInPeriod) {
                    errors.to.addError(t("Sick leaves are limited to") + " " + selfClarificationDaysInPeriod + " " + t("Days"));
                }
                if (formData.to && moment(formData.to).isAfter(moment().add(selfClarificationDaysInPeriod, 'days'))) {
                    errors.to.addError(t("Future sick leaves days are limited to") + " " + selfClarificationDaysInPeriod + " " + t("Days"));
                }
                if (formData.from && moment(formData.from).isBefore(moment().add(-selfClarificationCycleInMonths, 'months'))) {
                    errors.from.addError(t("Cannot apply sick leave for past cycles"));
                }
            }
            if (formData.absence_type === DEFAULT_ABSENCE_TYPES.IA_SICK_LEAVE) {
                if (formData.total_days > IASickLeaveMaxDaysInPeriod) {
                    errors.to.addError(t("Sick leaves are limited to") + " " + IASickLeaveMaxDaysInPeriod + " " + t("Days"));
                }
                if (formData.to && moment(formData.to).isAfter(moment().add(IASickLeaveMaxDaysInPeriod, 'days'))) {
                    errors.to.addError(t("Future sick leaves days are limited to") + " " + IASickLeaveMaxDaysInPeriod + " " + t("Days"));
                }
                if (formData.from && moment(formData.from).isBefore(moment().add(-selfClarificationCycleInMonths, 'months'))) {
                    errors.from.addError(t("Cannot apply sick leave for past cycles"));
                }
            }
        }

        if (formData.absence_type && formData.absence_type === DEFAULT_ABSENCE_TYPES.MEDICAL_LEAVE) {
            if (formData?.data?.doctor_certification_date) {
                const certDate = moment(formData.data.doctor_certification_date).format('YYYY-MM-DD');
                const fromDate = moment(formData?.from).format('YYYY-MM-DD');
                const toDate = moment(formData?.to).format('YYYY-MM-DD');
                if (!(certDate >= fromDate && certDate <= toDate)) {
                    errors?.data?.doctor_certification_date?.addError(t("Doctor Certificate should be between \"From date\" and \"To date\""))
                }
            }
            if (formData?.data?.medical_certification_percent > 100 || !formData?.data?.medical_certification_percent) {
                errors?.data?.medical_certification_percent?.addError(t('Medical certificate percentage should be between 1 to 100'))
            }
        }
        if (!formData.employement_grade || isNaN(formData.employement_grade) || formData.employement_grade < 1 || formData.employement_grade > 100) {
            errors?.absence_info_group_error?.addError(t("You're attempting to register more leave hours than the standard daily working hours set by your company. Please review and adjust the time accordingly."));
        }

        return errors;
    }

    getBack = () => {
        const { page, lastInteractedId } = this.props.router.location.state || {};
        this.props.router.navigate('/admin/absences', { state: { page: page, isEdit: true, lastInteractedId } });
    }

    updateSchema = (all, oldData) => {
        const { t } = this.props;
        const { schema, formData, uiSchema } = all;
        const { config } = this.props.commonStore;
        const _schema = cloneDeep(schema);
        let _uiSchema = cloneDeep(uiSchema);
        let shouldUpdateSchema = false;
        let shouldUpdateUiSchema = false;
        const { absenceStore } = this.props;

        if (absenceStore.userBreakDetails?.autoSplitMessage) {
            _uiSchema.total_days = {
                ..._uiSchema.total_days,
                'ui:help': (
                    <span style={{ color: 'green', fontWeight: 700 }}>
                        {absenceStore.userBreakDetails.autoSplitMessage}
                    </span>
                )
            };
            shouldUpdateUiSchema = true;
        } else {
            if (_uiSchema.total_days?.['ui:help']) {
                delete _uiSchema.total_days['ui:help'];
                shouldUpdateUiSchema = true;
            }
        }

        if (formData.user_id) {
            _uiSchema.project_id = {
                ..._uiSchema.project_id,
                'ui:widget': 'ProjectsWidget',
                'ui:user_id': formData.user_id
            };
            shouldUpdateUiSchema = true;
        }

        if (formData.user_id && _uiSchema.absence_type?.['ui:user_id'] !== formData.user_id) {
            _uiSchema.absence_type = {
                ..._uiSchema.absence_type,
                'ui:user_id': formData.user_id
            };
            shouldUpdateUiSchema = true;
        }
        if (formData?.absence_type === DEFAULT_ABSENCE_TYPES.PARENTAL_LEAVE && _schema.required.indexOf('child') === -1) {
            _schema.required.push('child');
            shouldUpdateSchema = true;
        } else {
            if (formData?.absence_type !== 'child' && _schema.required.indexOf('child') !== -1) {
                _schema.required.splice(_schema.required.indexOf('child'), 1);
                shouldUpdateSchema = true;
            }
        }

        const payrollSectionIndex = _uiSchema['ui:layout']?.findIndex(section =>
            section['ui:key'] === 'payroll-integration-section'
        );
        const isMedicalLeave = formData?.absence_type === DEFAULT_ABSENCE_TYPES.MEDICAL_LEAVE;
        const isVismaIntegration = config?.integration_details?.product === payrollIntegrationOptions.VISMA;
        const payrollSectionTitle = isVismaIntegration
            ? 'Payroll integration info'
            : 'Payroll integration info (Optional)';

        if (!isMedicalLeave && payrollSectionIndex > -1) {
            _uiSchema['ui:layout'].splice(payrollSectionIndex, 1);
            shouldUpdateUiSchema = true;
        } else if (isMedicalLeave && payrollSectionIndex === -1) {
            const commentsIndex = _uiSchema['ui:layout'].findIndex(section => 'comments' in section);
            const insertAt = commentsIndex > -1 ? commentsIndex : _uiSchema['ui:layout'].length;
            _uiSchema['ui:layout'].splice(insertAt, 0, {
                'ui:key': 'payroll-integration-section',
                'ui:subtitle': payrollSectionTitle,
                'ui:className': 'custom_style_integration_details py-2',
                data: { xs: 12, sm: 8 },
            });
            shouldUpdateUiSchema = true;
        } else if (isMedicalLeave && payrollSectionIndex > -1 && _uiSchema['ui:layout'][payrollSectionIndex]['ui:subtitle'] !== payrollSectionTitle) {
            _uiSchema['ui:layout'][payrollSectionIndex]['ui:subtitle'] = payrollSectionTitle;
            shouldUpdateUiSchema = true;
        }

        if (isMedicalLeave) {
            if (!_uiSchema.data) {
                _uiSchema.data = {};
            }
            _uiSchema.data.medical_certification_percent = {
                ..._uiSchema.data.medical_certification_percent,
                'ui:disabled': false
            };
            _uiSchema.data.doctor_certification_date = {
                ..._uiSchema.data.doctor_certification_date,
                'ui:disabled': false
            };
            shouldUpdateUiSchema = true;
        }
        else {
            if (!_uiSchema.data) {
                _uiSchema.data = {};
            }
            _uiSchema.data.medical_certification_percent = {
                ..._uiSchema.data.medical_certification_percent,
                'ui:disabled': true
            };
            _uiSchema.data.doctor_certification_date = {
                'ui:disabled': true
            };
            shouldUpdateUiSchema = true;
        }

        // Handle schedule help text
        if (absenceStore.isFromSchedule) {
            _uiSchema.start_time = {
                ..._uiSchema.start_time,
                'ui:help': t('Default Time from Schedule')
            };
            _uiSchema.end_time = {
                ..._uiSchema.end_time,
                'ui:help': t('Default Time from Schedule')
            };
            shouldUpdateUiSchema = true;
        } else {
            if (_uiSchema.start_time?.['ui:help']) {
                delete _uiSchema.start_time['ui:help'];
            }
            if (_uiSchema.end_time?.['ui:help']) {
                delete _uiSchema.end_time['ui:help'];
            }
            shouldUpdateUiSchema = true;
        }

        if (formData?.absence_type === DEFAULT_ABSENCE_TYPES.SOCIAL_WELFARE_LEAVE) {
            if (!_schema.required.includes('social_welfare_leave_type')) {
                _schema.required.push('social_welfare_leave_type');
            }
            shouldUpdateSchema = true;
        } else {
            _schema.required = _schema.required.filter(e => e !== 'social_welfare_leave_type');
            shouldUpdateSchema = true;
        }

        // Handle time field disabling based on absence_type
        if (formData.absence_type) {
            const filteredDefaultAbsenceTypes = Object.values(DEFAULT_ABSENCE_TYPES).filter(type => type !== DEFAULT_ABSENCE_TYPES.MEDICAL_LEAVE && type !== DEFAULT_ABSENCE_TYPES.SOCIAL_WELFARE_LEAVE);
            const shouldDisableTime = filteredDefaultAbsenceTypes.includes(formData.absence_type);

            _uiSchema.start_time = {
                ..._uiSchema.start_time,
                'ui:disabled': shouldDisableTime
            };
            _uiSchema.end_time = {
                ..._uiSchema.end_time,
                'ui:disabled': shouldDisableTime
            };
            _uiSchema.employement_grade = {
                ..._uiSchema.employement_grade,
                'ui:disabled': shouldDisableTime
            };

            shouldUpdateUiSchema = true;
        }

        const absenceInfoIndex = _uiSchema['ui:layout']?.findIndex(section =>
            section['ui:subtitle']?.includes('Absence Information')
        );

        if (absenceInfoIndex > -1) {
            const standardHoursExists = _uiSchema['ui:layout']?.some(section => {
                return section['ui:key'] === 'standard-working-hours-section';
            });
            const standardHours = parseFloat(absenceStore.standardCompanyTimeInMins / 60).toFixed(2)
            const userBreakDetails = absenceStore.currentEntity.absence.userBreakDetails;
            const subtitleText = t('Standard Working Hours') + ` (${standardHours} ${t('Hours/day')})` + (userBreakDetails?.message ? ' | ' + userBreakDetails?.message : '');

            if (!standardHoursExists) {
                const standardHoursSection = {
                    'ui:subtitle': subtitleText,
                    'ui:key': 'standard-working-hours-section',
                };

                _uiSchema['ui:layout'].splice(absenceInfoIndex + 1, 0, standardHoursSection);
                shouldUpdateUiSchema = true;
            } else {
                const existingIndex = _uiSchema['ui:layout']?.findIndex(section =>
                    section['ui:key'] === 'standard-working-hours-section'
                );

                if (existingIndex > -1) {
                    _uiSchema['ui:layout'][existingIndex]['ui:subtitle'] = subtitleText;
                    shouldUpdateUiSchema = true;
                }
            }
        }

        if (shouldUpdateSchema) {
            this.setState({ schema: _schema });
        }
        if (shouldUpdateUiSchema) {
            this.setState({ uiSchema: _uiSchema });
        }
    }

    render() {
        const { absenceStore, add, t } = this.props;
        const { loading, currentEntity } = absenceStore;
        const { schema, uiSchema, formTimeStamp, userPromptModal } = this.state;
        const { user_type, id: userId } = this.props.userStore.currentUser;
        let getId = (this.props.router.location.state && this.props.router.location.state.absence) || null;
        let allow = (user_type === 'admin' || user_type === 'pm')
            && customerFeature('pm_allow_editing_all')
            && !customerFeature('disallow_absences_vacations_not_self');
        allow = !add && currentEntity && currentEntity.absence && currentEntity.absence.user_id !== userId && !allow;

        if (currentEntity && currentEntity.absence && currentEntity.absence.status === 'approved') allow = true;
        if (currentEntity && currentEntity.absence && currentEntity.absence.absence_payment_per_day > 0) {
            currentEntity.absence.absence_total_payment = currentEntity.absence.absence_payment_per_day * currentEntity.absence.total_days;
        }

        if (loading) return <LoadingSpinner />;

        return (
            <div className="primary-page">
                <AbsencesTotals
                    key={`${currentEntity?.absence?.user_id}-${moment().isBefore(moment(currentEntity?.absence?.from)) ? currentEntity?.absence?.from : "current"}`}
                    user_id={currentEntity?.absence?.user_id !== userId ? currentEntity?.absence?.user_id : null}
                    for_date={currentEntity?.absence?.from}
                    to_date={currentEntity?.absence?.to}
                    edit_absence_id={getId}
                    absenceStatus={currentEntity?.absence?.status}
                    forceReloadForm={() => {
                        this.setState({
                            formTimeStamp: Date.now(),
                        }, () => {
                            if (!getId) return;
                            if (this.props.absenceStore.currentEntity && this.props.absenceStore.currentEntity.absence) {
                                const absence = this.props.absenceStore.currentEntity.absence;
                                this.props.absenceStore.recalc(absence, this.defaultHolidaysForVacation, this.enableDefaultHolidaysForAbsence, getId);
                                const all = { formData: absence, schema: this.state.schema, uiSchema: this.state.uiSchema };
                                this.updateSchema(all);
                            }
                        });
                    }}
                />
                <GenericForm
                    recomputeFields={['gps_data']}
                    key={formTimeStamp}
                    entity={currentEntity.absence}
                    uiSchema={uiSchema}
                    schema={schema}
                    translationScope="forms.absence"
                    onChange={async data =>
                        absenceStore.recalc(data, this.defaultHolidaysForVacation, this.enableDefaultHolidaysForAbsence, getId)
                    }
                    onSave={this.onSave}
                    listUrl={this.getListUrl()}
                    isAdding={!!this?.props?.router?.location?.state?.absence ? true : false}
                    disallowSave={allow}
                    customvalidate={(formData, errors) => this.validateAbsence(formData, errors)}
                    goBack={() => this.getBack()}
                    updateSchema={this.updateSchema}
                />

                <UserPromptModal
                    source={userPromptModal.source}
                    header={userPromptModal.header}
                    showModal={userPromptModal.showModal}
                    sourceInfo={userPromptModal.sourceInfo}
                    actionMethods={userPromptModal.actionMethods}
                />
            </div>
        );
    }
}

export default inject('absenceStore', 'commonStore', 'userStore', 'projectStore', 'scheduleStore')(applicationRouter(withLocalization(observer(AbsencesForm))));